import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';

import type { BillingInterval, Plan } from '../../types/types';
import PlanSelect from '../PlanSelect';
import { supabase } from '../../lib/supabase';
import type { ViewOrganisation } from '../../pages/MySettings';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';

interface SubscriptionUpgradeModalProps {
  show: boolean;
  onHide: () => void;
  organisation: ViewOrganisation | null;
}

export const SubscriptionUpgradeModal: React.FC<
  SubscriptionUpgradeModalProps
> = ({ show, onHide, organisation }) => {
  // const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const { data } = await supabase
        .from('plans')
        .select('*')
        .order('tier', { ascending: true });

      setPlans(data ?? []);
      setLoadingPlans(false);
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = async (
    planId: string,
    billingInterval: BillingInterval,
  ) => {
    setError(null);
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;

    // setSelectedPlan(plan);

    await handleUpgrade(plan, billingInterval);
  };

  const handleUpgrade = async (
    plan: Plan,
    billingInterval: BillingInterval,
  ) => {
    setError(null);
    if (!organisation) return;

    console.log('plan selected for upgrade:', plan);
    console.log('organisation:', organisation);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // If organisation already has Stripe customer -> open portal
      if (organisation.tier && organisation.billing_customer_id) {
        const { data, error } = await supabase.functions.invoke(
          'create-portal-session',
          {
            body: {
              customer_id: organisation.billing_customer_id,
            },
          },
        );

        if (error) throw error;

        window.location.href = data.url;
        return;
      }

      setLoadingPlans(true);

      const stripeLookupKey =
        billingInterval === 'yearly'
          ? plan.stripe_lookup_key_yearly
          : plan.stripe_lookup_key;

      if (!stripeLookupKey) {
        setError('Plan not configured');
        setLoadingPlans(false);
        return;
      }

      // Otherwise create checkout session
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        {
          body: {
            stripe_lookup_key: stripeLookupKey,
            organisation_id: organisation.organisation_id,
            email: user?.email,
            user_id: organisation.profile_id,
            flow: 'upgrade',
            plan_id: plan.id,
          },
        },
      );

      if (error) throw error;

      window.location.href = data.url;
    } catch (err) {
      captureSupabaseError(err, { op: 'subscription.upgrade' });
      setError('Failed to upgrade subscription');
      console.error('Upgrade failed:', err);
    }
  };

  const handleManageSubscription = async () => {
    tracking.log('UI', 'manage_subscription', {
      organisationId: organisation?.organisation_id,
    });
    setError(null);
    if (!organisation?.billing_customer_id) return;

    setLoadingPlans(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        'create-portal-session',
        {
          body: {
            customer_id: organisation.billing_customer_id,
          },
        },
      );

      console.log('Manage subscription data:', data);

      if (error) throw error;

      window.location.href = data.url;
    } catch (err) {
      captureSupabaseError(err, { op: 'subscription.manage' });
      setError(
        'Failed to open subscription management. Please try again later',
      );
      console.error('Failed to open subscription management:', err);
    }
  };

  const closeModal = () => {
    setError(null);
    // setSelectedPlan(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={closeModal} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Upgrade Your Subscription</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-5">
        {loadingPlans ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <p>Please select a new plan to upgrade your subscription.</p>

            {error && <Alert variant="danger">{error}</Alert>}

            <PlanSelect
              onSelect={handlePlanSelect}
              plans={plans}
              currentPlanTier={
                organisation?.tier ? parseInt(organisation.tier) : null
              }
            />
            {parseInt(organisation?.tier || '1') !== 1 && (
              <div className="d-flex align-items-center justify-content-center m-4">
                To manage your existing subscription, including cancellations or
                billing details, please use this link:
                <Button
                  variant="link"
                  className="p-0 ms-3"
                  onClick={() => handleManageSubscription()}
                >
                  Manage subscription
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
